import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import RouteNames from "../../routes/type";
import { BackButton, Button } from "../../components";
import { classNames, getCachedMppId, navigateTo } from "../../helper";
import { claimStepsData } from "./types";
import { PiCheckBold } from "react-icons/pi";
import { FormProvider, useForm } from "react-hook-form";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import CustomerInfo from "./CustomerInfo";
import PurchaseInfo from "./PurchaseInfo";
import DamageDetails from "./DamageDetails";
import UploadDocuments from "./UploadDocuments";
import ReviewDetails from "./ReviewDetails";
import SubmitClaim from "./SubmitClaim";
import { connect, useDispatch } from "react-redux";
import {
  cleanUpClaim,
  submitClaim,
  updateClaimState,
} from "../../store/mattress-claim/actions";
import { RootState } from "../../store/root-reducer";
import { ClaimScope } from "../../store/mattress-claim/types";
import { isScopeLoading } from "../../store/scope-loading/helpers";
import Loader from "../../components/Loader";

function CreateClaim({ loading, claim }) {
  let [searchParams] = useSearchParams();
  const formMethods = useForm({
    defaultValues: {
      customerInfo: claim.customerInfo,
      purchaseInfo: claim.purchaseInfo,
      damageInfo: claim.damageInfo,
    },
  });
  const { handleSubmit, getValues } = formMethods;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stepId: number = parseInt(searchParams.get("step") || "");
  const claimId: string = searchParams.get("id") || "";

  useEffect(() => {
    if ((!stepId && !/^[0-9]*$/.test(`${stepId}`)) || stepId > 6 || !claimId) {
      const parsed = queryString.parse(window.location.search);
      navigate(
        `/${RouteNames.CREATE_CLAIM}?${queryString.stringify({
          ...parsed,
          step: 1,
        })}`,
        { replace: true }
      );
    }
    return () => {};
  }, [claimId, navigate, stepId]);

  const currentStep = claimStepsData.find((item) => item.step_num === stepId);

  const navigateToStep = (step, parms = {}) => {
    const parsed = queryString.parse(window.location.search);
    window.scrollTo(0, 0);
    navigateTo(
      `/${RouteNames.CREATE_CLAIM}?${queryString.stringify({
        ...parsed,
        ...parms,
        step: step ?? stepId + 1,
      })}`
    );
  };

  const handleNext = () => {
    const values = getValues();
    switch (stepId) {
      case 1:
        const id = getCachedMppId();
        dispatch(
          updateClaimState({
            id,
            customerInfo: structuredClone(values?.customerInfo || {}),
          })
        );
        navigateToStep(2, { id });
        break;

      case 2:
        delete values.purchaseInfo.invoice_image;
        dispatch(
          updateClaimState({
            purchaseInfo: {
              ...claim.purchaseInfo,
              ...structuredClone(values?.purchaseInfo || {}),
            },
          })
        );
        navigateToStep(3);
        break;

      case 3:
        dispatch(
          updateClaimState({
            damageInfo: structuredClone(values?.damageInfo || {}),
          })
        );
        navigateToStep(4);
        break;

      case 4:
        navigateToStep(5);
        break;

      case 5:
        dispatch(submitClaim());
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = true;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  if (stepId === 6) {
    return (
      <Loader isLoading={loading}>
        <SubmitClaim key="stepId6" />
      </Loader>
    );
  }

  return (
    <div className="px-4 space-y-3">
      <div className="mt-4 flex justify-between items-center">
        <BackButton
          onClick={() => {
            if (stepId === 1) {
              navigate("/");
              dispatch(cleanUpClaim());
            } else {
              navigateToStep(stepId - 1);
            }
          }}
          text={stepId === 1 ? "Home" : "Back"}
        />
        <div className="flex flex-row gap-2">
          {claimStepsData.map(({ step_num: step }) => {
            const isCompleted = step < stepId;
            const isDraft = step > stepId;
            return (
              <button
                disabled
                key={step}
                className={classNames(
                  "size-7 flex rounded-full justify-center items-center text-base",
                  isDraft
                    ? "bg-[#009CAA] bg-opacity-15 text-[#009CAA] "
                    : "bg-[#009CAA] text-white"
                )}
              >
                {isCompleted ? <PiCheckBold size={16} color="white" /> : step}
              </button>
            );
          })}
        </div>
      </div>
      <h3 className="text-2xl font-semibold">{t(currentStep?.title)}</h3>
      <form onSubmit={handleSubmit(handleNext)}>
        <Loader isLoading={loading} />
        <FormProvider {...formMethods}>
          {stepId === 1 && <CustomerInfo key="stepId1" />}
          {stepId === 2 && <PurchaseInfo key="stepId2" />}
          {stepId === 3 && <DamageDetails key="stepId3" />}
          {stepId === 4 && <UploadDocuments key="stepId4" />}
          {stepId === 5 && <ReviewDetails key="stepId4" />}
        </FormProvider>
        <Button className="my-6" submit isLoading={loading}>
          {stepId === 5 ? t("Submit") : t("Next")}
        </Button>
      </form>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  loading: isScopeLoading(state.scopeLoading, ClaimScope),
  claim: state?.claim || {},
});

export default connect(mapStateToProps)(CreateClaim);
